import React from 'react';
import { Pencil, Trash2, Wand2 } from 'lucide-react';
import { KEY_STATE, KEY_STATE_SPECS } from '../catalog/keyState';
import type { KeyState } from '../catalog/keyState';
import { DEVICE_ANSWER, STATE_STATUS } from '../catalog/status';
import { formatValue } from '../catalog/value';
import { loadsFor } from '../model';
import { findTarget } from '../targets';
import type { MappingTarget } from '../targets';
import type { KeyStateReport } from '../stored';
import { StatusChip } from './StatusChip';

export interface StateRowProps {
  readonly report: KeyStateReport;
  readonly targets: readonly MappingTarget[];
  readonly onEdit: (state: KeyState) => void;
  readonly onClear: (state: KeyState) => void;
  readonly onMirror: (from: KeyState) => void;
}

/** One output as it reads in a state's summary. */
const loadSummary = (targets: readonly MappingTarget[], targetId: string, value: number): string => {
  const target = findTarget(targets, targetId);
  if (target === undefined) return `Missing output (${targetId})`;
  return `${target.label} · ${formatValue(target.valueKind, value)}`;
};

/**
 * What the device answered, when that is not what this console would write.
 *
 * Shown verbatim: a reply the console cannot read as a mapping is exactly the
 * case where its own summary would be a guess, and the raw entry is what the
 * technician has to compare against the firmware's log.
 */
const deviceHolds = (report: KeyStateReport): string | null => {
  const { answer } = report;
  if (answer.kind === DEVICE_ANSWER.UNKNOWN || answer.kind === DEVICE_ANSWER.ABSENT) return null;
  if (report.status === STATE_STATUS.MATCHES) return null;
  if (answer.entry !== null) return JSON.stringify(answer.entry);
  const reading = answer.reading;
  if (reading === null) return null;
  const actions = reading.actions.map((action) => `${action.field}:${action.raw}`).join(' ');
  return actions === '' ? 'an entry with no actions' : actions;
};

/**
 * One half of a key: what it does, and how that stands against the device.
 *
 * A state with nothing in it is shown rather than hidden — "this key does
 * nothing when released" is configuration, not absence. The mirror action is
 * offered only where it means something: a press copied onto its release, with
 * every output at its off value.
 */
export const StateRow: React.FC<StateRowProps> = ({
  report,
  targets,
  onEdit,
  onClear,
  onMirror,
}) => {
  const spec = KEY_STATE_SPECS[report.state];
  const loads = loadsFor(report.key, report.state);
  const held = deviceHolds(report);

  return (
    <div className="rounded-xl border border-[#E0E0E0] p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-[#333333]">{spec.label}</span>
            <StatusChip status={report.status} />
          </div>
          <p className="mt-0.5 text-xs text-[#666666]">{spec.hint}</p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {report.state === KEY_STATE.PRESS && loads.length > 0 && (
            <button
              type="button"
              title="Copy onto release, every output off"
              aria-label="Copy onto release, every output off"
              onClick={() => onMirror(report.state)}
              className="rounded-full p-2 text-[#666666] transition-colors hover:bg-[#F5F5F5] hover:text-[#D4A054]"
            >
              <Wand2 className="h-4 w-4" />
            </button>
          )}
          {loads.length > 0 && (
            <button
              type="button"
              title="Remove every output from this state"
              aria-label="Remove every output from this state"
              onClick={() => onClear(report.state)}
              className="rounded-full p-2 text-[#666666] transition-colors hover:bg-[#F5F5F5] hover:text-[#C0392B]"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
          <button
            type="button"
            onClick={() => onEdit(report.state)}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#E0E0E0] px-3 py-1.5 text-xs font-medium text-[#333333] transition-colors hover:border-[#D4A054] hover:text-[#D4A054]"
          >
            <Pencil className="h-3.5 w-3.5" />
            Edit
          </button>
        </div>
      </div>

      {loads.length > 0 ? (
        <ul className="mt-3 space-y-1">
          {loads.map((load) => (
            <li key={load.targetId} className="text-sm text-[#333333]">
              {loadSummary(targets, load.targetId, load.value)}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-3 text-sm text-[#999999]">
          Nothing happens on {spec.label.toLowerCase()}.
        </p>
      )}

      {held !== null && (
        <p className="mt-2 break-words rounded-lg bg-[#F5F5F5] px-3 py-2 font-mono text-xs text-[#666666]">
          Device holds {held}
        </p>
      )}
    </div>
  );
};

export default StateRow;
